import React from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

interface ChangeStateData {
  new: number;
  assess: number;
  authorize: number;
  scheduled: number;
  implement: number;
  review: number;
  closed: number;
  cancelled: number;
  on_hold: number;
}

interface ChangeStateBarChartProps {
  data: ChangeStateData;
  onBarClick?: (state: string) => void;
}

const stateLabels: Record<string, string> = {
  new: 'New',
  assess: 'Assess',
  authorize: 'Authorize',
  scheduled: 'Scheduled',
  implement: 'Implement',
  review: 'Review',
  closed: 'Closed',
  cancelled: 'Cancelled',
  on_hold: 'On Hold',
};

const stateColors: Record<string, string> = {
  new: 'rgba(59, 130, 246, 0.8)',      // blue
  assess: 'rgba(245, 158, 11, 0.8)',   // amber
  authorize: 'rgba(147, 51, 234, 0.8)', // purple
  scheduled: 'rgba(6, 182, 212, 0.8)', // cyan
  implement: 'rgba(249, 115, 22, 0.8)', // orange
  review: 'rgba(236, 72, 153, 0.8)',   // pink
  closed: 'rgba(16, 185, 129, 0.8)',   // green
  cancelled: 'rgba(239, 68, 68, 0.8)', // red
  on_hold: 'rgba(156, 163, 175, 0.8)', // gray
};

const ChangeStateBarChart: React.FC<ChangeStateBarChartProps> = ({ data, onBarClick }) => {
  const stateKeys = Object.keys(stateLabels);
  const labels = stateKeys.map(key => stateLabels[key]);
  const values = stateKeys.map(key => data[key as keyof ChangeStateData] || 0);
  const colors = stateKeys.map(key => stateColors[key]);
  const total = values.reduce((sum, val) => sum + val, 0);

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Change Requests',
        data: values,
        backgroundColor: colors,
        borderColor: colors.map(c => c.replace('0.8', '1')),
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y' as const,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.raw as number;
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${value} changes (${percentage}%)`;
          }
        }
      },
      datalabels: {
        display: (context: any) => context.dataset.data[context.dataIndex] > 0,
        color: '#fff',
        font: {
          weight: 'bold' as const,
          size: 10,
        },
        anchor: 'center' as const,
        align: 'center' as const,
        formatter: (value: number) => value > 0 ? value : '',
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { display: false },
        ticks: { precision: 0 }
      },
      y: {
        grid: { display: false }
      }
    },
    onClick: (_event: any, elements: any[]) => {
      if (elements.length > 0 && onBarClick) {
        const index = elements[0].index;
        onBarClick(stateKeys[index]);
      }
    },
  };

  return (
    <div id="chart-change-state-bar" className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">State-wise Change Requests</h3>
        <span className="text-xs text-gray-500">Click bar to view details</span>
      </div>
      <div className="h-72">
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
};

export default ChangeStateBarChart;
